import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Globe } from "lucide-react";
import { useCountry } from "../context/CountryContext";
import { toast } from "sonner";

/**
 * CountrySwitcher — dropdown sobre pour changer de juridiction.
 *
 * Props:
 *   - variant: "compact" (icône + drapeau) | "full" (drapeau + nom + numéros)
 *   - className: classes Tailwind additionnelles
 */
export default function CountrySwitcher({ variant = "compact", className = "" }) {
  const { country, countries, setCountry } = useCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handlePick = async (c) => {
    setOpen(false);
    if (c.id === country.id) return;
    await setCountry(c.id);
    toast.success(`Juridiction : ${c.flag} ${c.name}${c.is_beta ? " (Bêta)" : ""}`);
  };

  if (!countries.length) return null;

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="country-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`group flex items-center gap-2 rounded-sm border border-slate-300 bg-white text-slate-700 hover:border-slate-900 transition-colors ${
          variant === "full" ? "px-3 py-2 text-sm" : "px-2.5 h-9 text-sm"
        }`}
        data-testid="country-switcher-button"
        aria-label={`Pays actif : ${country.name}. Cliquer pour changer.`}
      >
        <span className="text-base leading-none" aria-hidden="true">{country.flag}</span>
        {variant === "full" ? (
          <>
            <span className="font-medium">{country.name}</span>
            <span className="text-xs text-slate-500">· Police {country.police}</span>
          </>
        ) : (
          <span className="hidden sm:inline font-medium">{country.id.toUpperCase()}</span>
        )}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-sm shadow-xl z-50 overflow-hidden max-sm:fixed max-sm:top-16 max-sm:right-2 max-sm:left-2 max-sm:w-auto max-sm:mt-0 max-sm:max-h-[80vh] max-sm:overflow-y-auto"
          data-testid="country-switcher-menu"
        >
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-[#B89C65]" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium">Choisir la juridiction</p>
          </div>
          <ul className="py-1">
            {countries.map((c) => {
              const active = c.id === country.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(c)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                      active ? "bg-amber-50" : ""
                    }`}
                    data-testid={`country-option-${c.id}`}
                  >
                    <span className="text-xl leading-none mt-0.5" aria-hidden="true">{c.flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">{c.name}</p>
                        {c.is_beta && (
                          <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">Bêta</span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Police {c.police} · Urgence {c.emergency} · {c.antiscam_org}
                      </p>
                    </div>
                    {active && <Check className="h-4 w-4 text-emerald-600 mt-1 flex-shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
            <p className="text-[10px] text-slate-400 leading-tight">
              Détection auto basée sur votre langue. Modifiable à tout moment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
